import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import '@chancenwerk/components/notifications/chancen-component-notification-card.js';
import { CampusUserGroup } from '@chancenwerk/db/CampusUserGroup.js';
import { CampusNotificationFilter } from '@chancenwerk/db/filter/CampusNotificationFilter.js';
import { CampusNotification } from '@chancenwerk/db/notifications/CampusNotification.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, filter, of, switchMap } from 'rxjs';

const studentSelector: BaseQLSelectionSet<Student> = {
  _id: 1,
  _class: 1,
  schoolInformation: {
    _class: 1,
    jahrgangsstufe: 1,
  },
  kpfs: {
    _id: 1,
    schoolID: 1,
  },
};

/**
 * @customElement chancen-component-notification-list
 */
@customElement('chancen-component-notification-list')
export class ChancenComponentNotificationList extends ChemistryLitElement {
  @property({ attribute: false }) public limit = new RXController(this, 0);

  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @state() private readonly _groupsOfUser = new RXController(
    this,
    this._currentUser.observable.pipe(
      switchMap(user => CampusUserGroup.getStudentGroupsForStudentID(user?._id ?? '')({ _class: 1, _id: 1 })),
    ),
  );

  @state() private readonly _filter = new RXController(
    this,
    combineLatest([this._currentUser.observable, this._groupsOfUser.observable]).pipe(
      filter(Boolean),
      switchMap(async ([user, groups]) => {
        let schoolIDs: string[] = [];
        let grade: Maybe<number>;
        if (user?._class === Student._class) {
          const student = await Student.getByID<Student>(user._id)(studentSelector);
          schoolIDs = (student?.kpfs?.filter(kpf => kpf.schoolID).map(kpf => kpf.schoolID) ?? []) as string[];
          grade = student?.schoolInformation.jahrgangsstufe;
        }

        return new CampusNotificationFilter({
          forEmployees: user?._class !== Student._class,
          forGrades: grade ? [grade] : ChancenwerkUser.availableGrades,
          forGroupIDs: groups?.map(g => g._id),
          forSchoolIDs: schoolIDs,
          userID: user?._id,
        });
      }),
    ),
  );

  @state() private readonly _notifications = new RXController(
    this,
    combineLatest([this._filter.observable, this.limit.observable]).pipe(
      filter(Boolean),
      switchMap(([filter, limit]) =>
        filter
          ? CampusNotification.subscribeNotificationsByFilterPaginated(0, limit, filter)(CampusNotification.allFields)
          : of([]),
      ),
    ),
  );

  override render() {
    return html`
      <cr-flex space="xs" crossaxis-center>
        ${!this._notifications.value?.length
          ? html` <cr-text>Im Augenblick gibt es keine aktuellen Neuigkeiten für dich.</cr-text> `
          : nothing}
        ${repeat(
          this._notifications.value ?? [],
          n => n._id,
          notification =>
            html`<chancen-component-notification-card
              ${css({ display: 'flex', flex: 1 })}
              .notification=${notification}
            ></chancen-component-notification-card>`,
        )}
      </cr-flex>
    `;
  }
}
